import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import {
  useRodales,
  useCrearRodal,
  useActualizarRodal,
  useEliminarRodal,
  useListaPredios
} from '../../hooks/useReferencias';
import { CrudTable, CrudForm } from '../../components/admin/crud';
import { formatHectareas } from '../../utils/numberUtils';
import {
  codigoField,
  descripcionField,
  estadoField,
  especieField,
  superficieField
} from '../../components/admin/crud/CrudForm';
import type { Rodal } from '../../types';
import type { CrearRodal } from '../../services/referenciasService';

// ===============================
// COMPONENTE PRINCIPAL
// ===============================

export const RodalesView: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  // ✅ PERMISOS UAU - Control granular de botones
  const canCreate = hasPermission('rodales.crear');
  const canEdit = hasPermission('rodales.editar');
  const canDelete = hasPermission('rodales.eliminar');

  // Estado local
  const [filtros, setFiltros] = useState({ page: 1, limit: 10 });
  const [formModal, setFormModal] = useState<{
    isOpen: boolean;
    mode: 'create' | 'edit';
    data?: Rodal;
  }>({
    isOpen: false,
    mode: 'create',
  });

  // Hooks de datos
  const { data, isLoading, error } = useRodales(filtros);
  const { data: predios, isLoading: cargandoPredios } = useListaPredios();
  const crearMutation = useCrearRodal();
  const actualizarMutation = useActualizarRodal();
  const eliminarMutation = useEliminarRodal();

  // Si los predios están cargando, no mostrar el formulario
  const formularioDisponible = !cargandoPredios && predios && predios.length > 0;

  // ===============================
  // CONFIGURACIÓN DE COLUMNAS
  // ===============================

  const columns = React.useMemo(() => [
    {
      key: 'codigo',
      title: t('common.code'),
      width: '120px',
    },
    {
      key: 'descripcion',
      title: t('common.description'),
      width: '200px',
    },
    {
      key: 'especie',
      title: t('common.species'),
      width: '130px',
      render: (especie: string) => especie || '-',
    },
    {
      key: 'año_plantacion',
      title: t('common.planting_year'),
      width: '120px',
      render: (año: number) => año || '-',
    },
    {
      key: 'superficie_ha',
      title: t('common.area_ha'),
      width: '130px',
      render: (superficie: number) => formatHectareas(superficie),
    },
    {
      key: 'estado',
      title: t('common.status'),
      width: '100px',
      render: (estado: string) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          estado === 'activo'
            ? 'bg-green-100 text-green-800'
            : 'bg-red-100 text-red-800'
        }`}>
          {estado === 'activo' ? t('admin.status_active') : t('admin.status_inactive')}
        </span>
      ),
    },
  ], [t]);

  // ===============================
  // CONFIGURACIÓN DINÁMICA DE FILTROS
  // ===============================

  const dynamicFilters = React.useMemo(() => {
    const baseFilters = [
      {
        key: 'search',
        label: t('admin.search_placeholder'),
        type: 'text' as const,
        placeholder: t('admin.search_placeholder'),
      },
      {
        key: 'especie',
        label: t('common.species'),
        type: 'text' as const,
        placeholder: t('admin.filter_by_species'),
      },
      {
        key: 'predio_id',
        label: t('admin.predios'),
        type: 'select' as const,
        options: predios?.map(predio => ({
          value: predio.id.toString(),
          label: `${predio.codigo} - ${predio.descripcion}`,
        })) || [],
      },
      {
        key: 'estado',
        label: t('common.status'),
        type: 'select' as const,
        options: [
          { value: 'activo', label: t('admin.status_active') },
          { value: 'inactivo', label: t('admin.status_inactive') },
        ],
      },
    ];

    return baseFilters;
  }, [predios, t]);

  // ===============================
  // CONFIGURACIÓN DINÁMICA DE FORMULARIO
  // ===============================

  const formFields = React.useMemo(() => {
    const predioField = {
      name: 'predio_id',
      label: t('admin.predios'),
      type: 'select' as const,
      options: predios?.map(predio => ({
        value: predio.id.toString(),
        label: `${predio.codigo} - ${predio.descripcion}`,
      })) || [],
    };

    const añoPlantacionField = {
      name: 'año_plantacion',
      label: t('common.planting_year'),
      type: 'number' as const,
      placeholder: '2020',
      min: 1950,
      max: new Date().getFullYear(),
    };

    return [
      codigoField,
      descripcionField,
      especieField,
      añoPlantacionField,
      superficieField,
      predioField,
      estadoField,
    ];
  }, [predios, t]);

  // ===============================
  // MANEJADORES DE EVENTOS
  // ===============================

  const handleFiltersChange = (newFilters: Record<string, any>) => {
    setFiltros({ ...filtros, ...newFilters });
  };

  const handlePageChange = (page: number) => {
    setFiltros({ ...filtros, page });
  };

  const handleAdd = () => {
    if (!canCreate) {
      toast.error(t('errors.no_permission'));
      return;
    }
    if (!formularioDisponible) {
      toast.error(t('admin.no_predios_available'));
      return;
    }
    setFormModal({
      isOpen: true,
      mode: 'create',
    });
  };

  const handleEdit = (rodal: Rodal) => {
    setFormModal({
      isOpen: true,
      mode: 'edit',
      data: rodal,
    });
  };

  const handleDelete = async (rodal: Rodal) => {
    try {
      await eliminarMutation.mutateAsync(rodal.id);
      toast.success(t('admin.delete_success'));
    } catch (error) {
      toast.error(t('errors.delete_error'));
      console.error('Error eliminando rodal:', error);
    }
  };

  const handleFormSubmit = async (datos: CrearRodal) => {
    try {
      if (formModal.mode === 'create') {
        await crearMutation.mutateAsync(datos);
        toast.success(t('admin.create_success'));
      } else if (formModal.data) {
        await actualizarMutation.mutateAsync({
          id: formModal.data.id,
          datos,
        });
        toast.success(t('admin.update_success'));
      }

      setFormModal({ isOpen: false, mode: 'create' });
    } catch (error: any) {
      const message = error?.response?.data?.detail || t('errors.save_error');
      toast.error(message);
      console.error('Error en formulario rodal:', error);
    }
  };

  const handleFormClose = () => {
    setFormModal({ isOpen: false, mode: 'create' });
  };

  // ===============================
  // ESTADO DE CARGA DEL FORMULARIO
  // ===============================

  const isFormLoading = crearMutation.isLoading || actualizarMutation.isLoading;

  // ===============================
  // RENDER
  // ===============================

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center space-x-2 text-sm text-gray-500">
        <button
          onClick={() => navigate('/admin')}
          className="hover:text-gray-700 transition-colors"
        >
          {t('admin.administration')}
        </button>
        <span>/</span>
        <span className="text-gray-900 font-medium">{t('admin.rodales')}</span>
      </div>

      {/* Botón volver */}
      <div className="flex items-center">
        <button
          onClick={() => navigate('/admin')}
          className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center space-x-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          <span>{t('common.back_to_admin')}</span>
        </button>
      </div>

      {/* Tabla CRUD */}
      <CrudTable
        data={data}
        isLoading={isLoading}
        error={error as Error | null}
        columns={columns}
        filters={dynamicFilters}
        currentFilters={filtros}
        onFiltersChange={handleFiltersChange}
        onPageChange={handlePageChange}
        onAdd={handleAdd}
        onEdit={canEdit ? handleEdit : undefined}
        onDelete={canDelete ? handleDelete : undefined}
        title={t('admin.rodales_title')}
        addButtonText={t('admin.add')}
        canAdd={canCreate}
        canEdit={canEdit}
        canDelete={canDelete}
      />

      {/* Modal de formulario */}
      <CrudForm
        isOpen={formModal.isOpen}
        onClose={handleFormClose}
        onSubmit={handleFormSubmit}
        fields={formFields}
        title={formModal.mode === 'create' ? t('admin.create_rodal') : t('admin.edit_item')}
        submitText={formModal.mode === 'create' ? t('common.save') : t('common.save')}
        isLoading={isFormLoading}
        initialData={formModal.data}
        mode={formModal.mode}
      />
    </div>
  );
};

export default RodalesView;
